import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/db';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
import { ArrowLeft, Search, Key, RotateCcw } from 'lucide-react';
import { updateAdminStatus } from '../services/authService';

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState<string | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<User | null>(null);
  const [showVoucherResetModal, setShowVoucherResetModal] = useState(false);
  const [selectedUserForVoucherReset, setSelectedUserForVoucherReset] = useState<User | null>(null);
  const [resettingVoucherUserId, setResettingVoucherUserId] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      loadUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  // Check if current user is super admin
  const isSuperAdmin = currentUser?.isSuperAdmin === true;
  const isAdmin = currentUser?.isAdmin === true || isSuperAdmin;

  const loadUsers = async () => {
    try {
      // Call secure Edge Function for user data
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-admin-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          searchTerm: searchTerm
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch users');
      }

      const result = await response.json();
      setUsers(result.data.users);
      
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Fehler beim Laden der Benutzer: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'user' | 'admin' | 'super_admin') => {
    if (!isSuperAdmin) {
      alert('Nur Super-Admins können Benutzerrollen ändern.');
      return;
    }

    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) {
      alert('Benutzer nicht gefunden.');
      return;
    }

    // Prevent super-admin from modifying their own role
    if (userId === currentUser?.id) {
      alert('Sie können Ihre eigene Rolle nicht ändern.');
      return;
    }

    // Prevent modifying other super-admins
    if (targetUser.is_super_admin && newRole !== 'super_admin') {
      alert('Super-Admin-Rollen können nicht geändert werden.');
      return;
    }

    const confirmMessage = `Möchten Sie ${targetUser.name} wirklich zu "${
      newRole === 'super_admin' ? 'Super-Admin' : 
      newRole === 'admin' ? 'Admin' : 'Benutzer'
    }" machen?`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    setUpdatingUserId(userId);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-user-roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId: userId,
          newRole: newRole
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user role');
      }
      
      // Update local state optimistically
      const newIsAdmin = newRole === 'admin' || newRole === 'super_admin';
      const newIsSuperAdmin = newRole === 'super_admin';
      
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId 
          ? { ...user, is_admin: newIsAdmin, is_super_admin: newIsSuperAdmin }
          : user
      ));
      
      // Update filtered users
      setFilteredUsers(prevUsers => prevUsers.map(user =>
        user.id === userId
          ? { ...user, is_admin: newIsAdmin, is_super_admin: newIsSuperAdmin }
          : user
      ));

      alert(`Benutzerrolle erfolgreich aktualisiert.`);
    } catch (error) {
      console.error('Error updating user role:', error);
      alert(`Fehler beim Aktualisieren der Benutzerrolle: ${error.message}`);
      // Revert optimistic update on error
      loadUsers();
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleVoucherReset = (user: User) => {
    setSelectedUserForVoucherReset(user);
    setShowVoucherResetModal(true);
  };

  const confirmVoucherReset = async () => {
    if (!selectedUserForVoucherReset) return;

    setResettingVoucherUserId(selectedUserForVoucherReset.id);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-user-voucher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ targetUserId: selectedUserForVoucherReset.id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset voucher');
      }

      const result = await response.json();

      setUsers(prevUsers => prevUsers.map(u =>
        u.id === selectedUserForVoucherReset.id ? { ...u, voucher_code: null } : u
      ));

      alert(`Voucher-Zustand für ${selectedUserForVoucherReset.name} wurde erfolgreich zurückgesetzt.${result.data?.old_voucher_code ? ` (Alter Code: ${result.data.old_voucher_code})` : ''}`);
      setShowVoucherResetModal(false);
      setSelectedUserForVoucherReset(null);
    } catch (error) {
      console.error('Error resetting voucher:', error);
      alert(`Fehler beim Zurücksetzen des Vouchers: ${error.message}`);
    } finally {
      setResettingVoucherUserId(null);
    }
  };

  const getUserRoleDisplay = (user: any) => {
    if (user.is_super_admin) return 'Super-Admin';
    if (user.is_admin) return 'Admin';
    return 'Benutzer';
  };

  const getUserRoleColor = (user: any) => {
    if (user.is_super_admin) return 'bg-purple-100 text-purple-800';
    if (user.is_admin) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const handleResetPassword = async (user: User) => {
    setSelectedUserForReset(user);
    setShowResetModal(true);
  };

  const confirmResetPassword = async () => {
    if (!selectedUserForReset) return;

    setResettingPasswordUserId(selectedUserForReset.id);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reset-user-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId: selectedUserForReset.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset password');
      }

      const result = await response.json();

      alert(`Passwort-Zurücksetzungs-E-Mail wurde erfolgreich an ${selectedUserForReset.email} gesendet.`);
      setShowResetModal(false);
      setSelectedUserForReset(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(`Fehler beim Zurücksetzen des Passworts: ${error.message}`);
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-12">
      <div className="container mx-auto px-4">
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">Benutzerübersicht</h1>
          <div className="relative ml-auto w-64">
            <input
              type="text"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
        </div>

        {showResetModal && selectedUserForReset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Passwort zurücksetzen</h2>
              <p className="text-gray-700 mb-4">
                Möchten Sie das Passwort für <strong>{selectedUserForReset.name}</strong> ({selectedUserForReset.email}) zurücksetzen?
              </p>
              <p className="text-sm text-gray-600 mb-6">
                Eine E-Mail mit einem Link zum Zurücksetzen des Passworts wird an die E-Mail-Adresse des Benutzers gesendet.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setSelectedUserForReset(null);
                  }}
                  disabled={resettingPasswordUserId !== null}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={confirmResetPassword}
                  disabled={resettingPasswordUserId !== null}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {resettingPasswordUserId !== null ? 'Wird gesendet...' : 'Passwort zurücksetzen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showVoucherResetModal && selectedUserForVoucherReset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Voucher zurücksetzen</h2>
              <p className="text-gray-700 mb-2">
                Möchten Sie den Voucher-Zustand für <strong>{selectedUserForVoucherReset.name}</strong> ({selectedUserForVoucherReset.email}) wirklich zurücksetzen?
              </p>
              {selectedUserForVoucherReset.voucher_code && (
                <p className="text-sm text-gray-600 mb-2">
                  Aktueller Voucher-Code: <strong>{selectedUserForVoucherReset.voucher_code}</strong>
                </p>
              )}
              <p className="text-sm text-amber-700 bg-amber-50 rounded p-3 mb-6">
                Der Voucher-Code wird vom Benutzer entfernt und die interne Nutzungszuordnung bereinigt. Stripe-Daten bleiben unverändert. Auth-Account, Kursdaten und alle anderen Benutzerdaten bleiben erhalten.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowVoucherResetModal(false);
                    setSelectedUserForVoucherReset(null);
                  }}
                  disabled={resettingVoucherUserId !== null}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={confirmVoucherReset}
                  disabled={resettingVoucherUserId !== null}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {resettingVoucherUserId !== null ? 'Wird zurückgesetzt...' : 'Voucher zurücksetzen'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    E-Mail
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unternehmen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rolle
                  </th>
                  {isSuperAdmin && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rollen-Aktionen
                    </th>
                  )}
                  {isSuperAdmin && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Passwort
                    </th>
                  )}
                  {isSuperAdmin && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Voucher
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registriert am
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.company}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getUserRoleColor(user)}`}>
                        {getUserRoleDisplay(user)}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.id !== currentUser?.id && (
                          <div className="flex space-x-2">
                            {!user.is_super_admin && (
                              <>
                                <button
                                  onClick={() => handleRoleChange(user.id, 'user')}
                                  disabled={updatingUserId === user.id || (!user.is_admin && !user.is_super_admin)}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    !user.is_admin && !user.is_super_admin
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {updatingUserId === user.id ? '...' : 'Benutzer'}
                                </button>
                                <button
                                  onClick={() => handleRoleChange(user.id, 'admin')}
                                  disabled={updatingUserId === user.id || user.is_admin}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    user.is_admin && !user.is_super_admin
                                      ? 'bg-blue-200 text-blue-500 cursor-not-allowed'
                                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  }`}
                                >
                                  {updatingUserId === user.id ? '...' : 'Admin'}
                                </button>
                                <button
                                  onClick={() => handleRoleChange(user.id, 'super_admin')}
                                  disabled={updatingUserId === user.id || user.is_super_admin}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    user.is_super_admin
                                      ? 'bg-purple-200 text-purple-500 cursor-not-allowed'
                                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                  }`}
                                >
                                  {updatingUserId === user.id ? '...' : 'Super-Admin'}
                                </button>
                              </>
                            )}
                            {user.is_super_admin && (
                              <span className="text-xs text-gray-500 italic">
                                Geschützt
                              </span>
                            )}
                          </div>
                        )}
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-gray-500 italic">
                            Sie selbst
                          </span>
                        )}
                      </td>
                    )}
                    {isSuperAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleResetPassword(user)}
                          disabled={resettingPasswordUserId === user.id}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium hover:bg-orange-200 transition-colors disabled:opacity-50"
                        >
                          <Key className="h-3 w-3" />
                          {resettingPasswordUserId === user.id ? 'Wird gesendet...' : 'Zurücksetzen'}
                        </button>
                      </td>
                    )}
                    {isSuperAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleVoucherReset(user)}
                          disabled={resettingVoucherUserId === user.id}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                          title={user.voucher_code ? `Voucher: ${user.voucher_code}` : 'Kein Voucher gesetzt'}
                        >
                          <RotateCcw className="h-3 w-3" />
                          {resettingVoucherUserId === user.id ? 'Wird zurückgesetzt...' : 'Voucher reset'}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.course_progress?.allCompleted
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.course_progress?.allCompleted ? 'Abgeschlossen' : 'In Bearbeitung'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString('de-DE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;